import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Alert,
    Checkbox,
    Button,
    Card,
    Col,
    Empty,
    Image,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
    Modal,
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    HomeOutlined,
    PartitionOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useNavigate } from 'react-router-dom';
import AreaMap from '../components/Map/AreaMap';
import '../style/Areas.css';

const { Text, Title } = Typography;

// ─── Types ───────────────────────────────────────────────────────────────────

interface AreaType {
    id: number;
    name: string;
    code?: string;
}

interface Area {
    archivedAt?: string | null;
    id: number;
    name: string;
    code?: string;
    areaType?: AreaType | null;
    companyId: number;
    geom?: any;
    spaceType?: string;
    slamMapId?: number | null;
    createdAt?: string;
    parentAreaId?: number | null;
    parentArea?: { id: number; name: string } | null;
    children?: (Area | AddButtonRow)[];
    hierarchyDepth?: number;
    configurations?: Record<string, any> | null;
}

interface AddButtonRow {
    id: string;
    name: string;
    isAddButton: true;
    parentId: number;
    parentName: string;
    hierarchyDepth?: number;
}

interface SlamMapInfo {
    id: number;
    name: string;
    mapType: string;
    imageStoragePath?: string | null;
    imageUrl?: string | null;
    imageWidth?: number | null;
    imageHeight?: number | null;
    metersPerPixel?: number | null;
    geoAnchor?: {
        lat: number;
        lng: number;
        anchorPixelX: number;
        anchorPixelY: number;
        bearingDegrees: number;
    } | null;
    createdAt: string;
}

type TableRow = Area | AddButtonRow;
type SlamPlacementLike = { lat: number; lng: number; bearingDegrees: number };

const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS = 6371000;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const spaceTypeTag = (spaceType?: string) => {
    if (spaceType === 'indoor') return <Tag color="orange" style={{ fontSize: 10, marginLeft: 6 }}>indoor</Tag>;
    if (spaceType === 'mixed') return <Tag color="purple" style={{ fontSize: 10, marginLeft: 6 }}>mixed</Tag>;
    return null;
};

const spaceTypeIcon = (spaceType?: string) => {
    if (spaceType === 'indoor') return <HomeOutlined style={{ color: '#fa8c16', marginRight: 4 }} />;
    if (spaceType === 'mixed') return <PartitionOutlined style={{ color: '#722ed1', marginRight: 4 }} />;
    return null;
};

const isGpsCoordinate = (coord: any): boolean => {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    const [lng, lat] = coord;
    return Number.isFinite(lng) && Number.isFinite(lat)
        && lng >= -180 && lng <= 180
        && lat >= -90 && lat <= 90;
};

const isGpsGeometry = (geom: any): boolean => {
    if (!geom?.coordinates) return false;
    if (geom.type === 'Point') return isGpsCoordinate(geom.coordinates);
    if (geom.type === 'Polygon') return Array.isArray(geom.coordinates?.[0]) && geom.coordinates[0].every(isGpsCoordinate);
    if (geom.type === 'MultiPolygon') return Array.isArray(geom.coordinates?.[0]?.[0]) && geom.coordinates[0][0].every(isGpsCoordinate);
    return false;
};

const localMetersToGps = (mx: number, my: number, anchor: SlamPlacementLike): [number, number] => {
    const bearing = anchor.bearingDegrees * DEG_TO_RAD;
    const northM = my * Math.cos(bearing) - mx * Math.sin(bearing);
    const eastM = my * Math.sin(bearing) + mx * Math.cos(bearing);
    const lat = anchor.lat + (northM / EARTH_RADIUS) * (180 / Math.PI);
    const lng = anchor.lng + (eastM / (EARTH_RADIUS * Math.cos(anchor.lat * DEG_TO_RAD))) * (180 / Math.PI);
    return [lng, lat];
};

const convertLocalGeometryToGps = (geom: any, anchor: SlamPlacementLike): any | null => {
    if (!geom?.coordinates) return null;
    if (geom.type === 'Point') {
        const [mx, my] = geom.coordinates;
        if (!Number.isFinite(mx) || !Number.isFinite(my)) return null;
        return { type: 'Point', coordinates: localMetersToGps(mx, my, anchor) };
    }
    if (geom.type === 'Polygon' && Array.isArray(geom.coordinates?.[0])) {
        return {
            type: 'Polygon',
            coordinates: [geom.coordinates[0].map(([mx, my]: [number, number]) => localMetersToGps(mx, my, anchor))],
        };
    }
    if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates?.[0]?.[0])) {
        return {
            type: 'MultiPolygon',
            coordinates: geom.coordinates.map((poly: [number, number][][]) =>
                poly.map((ring) => ring.map(([mx, my]) => localMetersToGps(mx, my, anchor)))),
        };
    }
    return null;
};

const getSavedSlamPlacement = (area?: Area | null): SlamPlacementLike | null => {
    const saved = area?.configurations?.slamPlacement;
    if (saved?.lat == null || saved?.lng == null) return null;
    return {
        lat: Number(saved.lat),
        lng: Number(saved.lng),
        bearingDegrees: Number(saved.bearingDegrees ?? 0),
    };
};

const getSlamGeoAnchorPlacement = (slamMap?: SlamMapInfo | null): SlamPlacementLike | null => {
    if (!slamMap?.geoAnchor) return null;
    return {
        lat: Number(slamMap.geoAnchor.lat),
        lng: Number(slamMap.geoAnchor.lng),
        bearingDegrees: Number(slamMap.geoAnchor.bearingDegrees ?? 0),
    };
};

const getGpsGeometryForAreaMap = (area: Area | null | undefined, selectedArea: Area | null, slamMap: SlamMapInfo | null): any | null => {
    if (!area?.geom) return null;
    if (area.spaceType === 'indoor') {
        const anchor = getSavedSlamPlacement(area)
            || getSavedSlamPlacement(selectedArea)
            || getSlamGeoAnchorPlacement(slamMap);
        return anchor ? convertLocalGeometryToGps(area.geom, anchor) : null;
    }
    return isGpsGeometry(area.geom) ? area.geom : null;
};

const buildTree = (items: Area[]): Area[] => {
    const map: { [key: number]: Area } = {};
    const roots: Area[] = [];

    items.forEach(item => {
        if (item.spaceType !== 'root_location') {
            map[item.id] = { ...item, children: [] };
        }
    });

    items.forEach(item => {
        if (item.spaceType === 'root_location') return;

        const parentIsRootLocation = item.parentAreaId
            ? items.find(a => a.id === item.parentAreaId)?.spaceType === 'root_location'
            : false;

        if (item.parentAreaId && map[item.parentAreaId] && !parentIsRootLocation) {
            map[item.parentAreaId].children!.push(map[item.id]);
        } else {
            roots.push(map[item.id]);
        }
    });

    return roots;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Areas: React.FC = () => {
    const navigate = useNavigate();
    const [areas, setAreas] = useState<TableRow[]>([]);
    const [showArchived, setShowArchived] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedArea, setSelectedArea] = useState<Area | null>(null);
    const [viewState, setViewState] = useState({ longitude: -0.1278, latitude: 51.5074, zoom: 13, bearing: 0, pitch: 0 });
    const [areaTypeTemplates, setAreaTypeTemplates] = useState<Map<number, string>>(new Map());
    const [checkedAreaTypes, setCheckedAreaTypes] = useState<Set<number>>(new Set());
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const [slamMap, setSlamMap] = useState<SlamMapInfo | null>(null);
    const [slamMapLoading, setSlamMapLoading] = useState(false);
    const [slamBlobUrl, setSlamBlobUrl] = useState<string | null>(null);
    const slamBlobUrlRef = useRef<string | null>(null);

    // ── Find helpers ──────────────────────────────────────────────────────────

    const findAreaInTree = (areaId: number, areaList: TableRow[]): Area | null => {
        for (const area of areaList) {
            if ('isAddButton' in area && area.isAddButton) continue;
            const areaRecord = area as Area;
            if (areaRecord.id === areaId) return areaRecord;
            if (areaRecord.children && areaRecord.children.length > 0) {
                const found = findAreaInTree(areaId, areaRecord.children);
                if (found) return found;
            }
        }
        return null;
    };

    const findAreaPath = (areaId: number, areaList: TableRow[], path: number[] = []): number[] => {
        for (const area of areaList) {
            if ('isAddButton' in area && area.isAddButton) continue;
            const areaRecord = area as Area;
            const nextPath = [...path, areaRecord.id];
            if (areaRecord.id === areaId) return nextPath;
            if (areaRecord.children && areaRecord.children.length > 0) {
                const found = findAreaPath(areaId, areaRecord.children, nextPath);
                if (found.length > 0) return found;
            }
        }
        return [];
    };

    const collectExpandedKeys = (area: TableRow | null): string[] => {
        if (!area || ('isAddButton' in area && area.isAddButton)) return [];
        const areaRecord = area as Area;
        const keys = [String(areaRecord.id)];
        areaRecord.children?.forEach((child) => {
            keys.push(...collectExpandedKeys(child));
        });
        return keys;
    };

    // ── Data fetching ─────────────────────────────────────────────────────────

    const fetchAreaDetail = useCallback(async (id: number, currentAreas: Area[]) => {
        try {
            const res = await axios.get(ENDPOINTS.AREA_DETAIL(id), { withCredentials: true });
            const payload = res.data?.data || res.data;
            if (payload) {
                const areaInTree = findAreaInTree(id, currentAreas);
                if (areaInTree) {
                    setSelectedArea({ ...payload, children: areaInTree.children });
                } else {
                    setSelectedArea(payload);
                }
            }
        } catch (error) {
            console.warn('Failed to load area detail', error);
        }
    }, []);

    const fetchAreas = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true, params: { include_archived: showArchived ? 'true' : undefined } });
            const payload: Area[] = res.data?.data || res.data || [];
            const tree = buildTree(payload);
            setAreas(tree as TableRow[]);
            setSelectedArea(null);

            if (tree.length > 0 && 'id' in tree[0] && typeof tree[0].id === 'number') {
                fetchAreaDetail(tree[0].id, tree as Area[]);
                setExpandedRowKeys(collectExpandedKeys(tree[0]));
            }
        } catch (error) {
            console.error('Failed to load areas', error);
            message.error('Could not load areas.');
        } finally {
            setLoading(false);
        }
    }, [fetchAreaDetail, showArchived]);

    useEffect(() => { fetchAreas(); }, [fetchAreas]);

    // ── Fetch SLAM map when indoor/mixed area is selected ─────────────────────

    useEffect(() => {
        const isIndoorLike = selectedArea?.spaceType === 'indoor' || selectedArea?.spaceType === 'mixed';
        if (!selectedArea || !isIndoorLike) {
            setSlamMap(null);
            return;
        }
        let cancelled = false;
        setSlamMapLoading(true);
        setSlamMap(null);
        axios.get(ENDPOINTS.AREA_SLAM_MAP_LATEST(selectedArea.id), { withCredentials: true })
            .then(res => { if (!cancelled) setSlamMap(res.data?.data || null); })
            .catch(() => { if (!cancelled) setSlamMap(null); })
            .finally(() => { if (!cancelled) setSlamMapLoading(false); });
        return () => { cancelled = true; };
    }, [selectedArea]);

    // ── Fetch SLAM image as blob URL for Mapbox (avoids GCS CORS issues) ──────

    useEffect(() => {
        // Revoke previous blob URL
        if (slamBlobUrlRef.current) {
            URL.revokeObjectURL(slamBlobUrlRef.current);
            slamBlobUrlRef.current = null;
            setSlamBlobUrl(null);
        }
        if (!slamMap?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const imgResponse = await axios.get(ENDPOINTS.SLAM_MAP_IMAGE_STREAM(slamMap.id), {
                    withCredentials: true,
                    responseType: 'blob',
                });
                if (cancelled) return;
                const blobUrl = URL.createObjectURL(imgResponse.data);
                slamBlobUrlRef.current = blobUrl;
                setSlamBlobUrl(blobUrl);
            } catch (err) {
                console.error('[Areas] Failed to fetch SLAM image via proxy:', err);
            }
        })();
        return () => {
            cancelled = true;
            if (slamBlobUrlRef.current) {
                URL.revokeObjectURL(slamBlobUrlRef.current);
                slamBlobUrlRef.current = null;
            }
        };
    }, [slamMap]);

    // ── Delete ────────────────────────────────────────────────────────────────

    const handleDelete = useCallback(async (areaId: number, areaName: string) => {
        Modal.confirm({
            title: 'Delete Area',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to delete "${areaName}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await axios.delete(`${ENDPOINTS.AREAS_V2}/${areaId}`, { withCredentials: true });
                    message.success(`Area "${areaName}" deleted successfully`);
                    fetchAreas();
                } catch {
                    message.error('Failed to delete area. It may have dependencies.');
                }
            }
        });
    }, [fetchAreas]);

    const handleArchive = useCallback((area: Area) => {
        const archived = !area.archivedAt;
        Modal.confirm({
            title: archived ? `Archive "${area.name}"?` : `Restore "${area.name}"?`,
            content: archived
                ? 'This also archives its subareas and area-specific patterns. They will be hidden and excluded from Lightbox downloads. Measurements are preserved.'
                : 'Restore the parent first. Subareas and patterns must be restored separately.',
            onOk: async () => {
                try {
                    await axios.post(`${ENDPOINTS.AREAS_V2}/${area.id}/archive`, { archived }, { withCredentials: true });
                    message.success(archived ? 'Area archived' : 'Area restored');
                    await fetchAreas();
                } catch (err: any) {
                    message.error(err?.response?.data?.message || 'Could not update archive status');
                }
            },
        });
    }, [fetchAreas]);

    // ── Table columns ─────────────────────────────────────────────────────────

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: any) => {
                if (record.isAddButton) {
                    const depth = record.hierarchyDepth || 0;
                    return (
                        <div className="area-add-row list-create-row--inline" style={{ paddingLeft: depth * 16 }}>
                            <Button
                                type="text"
                                icon={<PlusOutlined />}
                                size="small"
                                className="area-add-button list-create-button list-create-button--inline"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/areas/new?parentId=${record.parentId}`);
                                }}
                            >
                                <span className="area-add-button-text">Add sub-area</span>
                            </Button>
                            <Text type="secondary" className="area-add-parent list-create-note">to {record.parentName}</Text>
                        </div>
                    );
                }
                const depth = record.hierarchyDepth || 0;
                return (
                    <div className="area-name-cell" style={{ paddingLeft: depth * 16 }}>
                        {depth > 0 && <span className="area-depth-marker" />}
                        {spaceTypeIcon(record.spaceType)}
                        <span>{text}</span>
                        {spaceTypeTag(record.spaceType)}
                    </div>
                );
            }
        },
        {
            title: 'Type',
            dataIndex: ['areaType', 'name'],
            key: 'areaType',
            render: (_: any, record: any) => {
                if (record.isAddButton) return null;
                return record.areaType?.name || '—';
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: any) => {
                if (record.isAddButton) return null;
                const hasChildren = record.children && record.children.some((c: any) => !c.isAddButton);
                return (
                    <Space>
                        <Button size="small" onClick={(e) => { e.stopPropagation(); handleArchive(record); }}>
                            {record.archivedAt ? 'Restore' : 'Archive'}
                        </Button>
                        {record.archivedAt && <Tag>Archived</Tag>}
                        <Button
                            size="small"
                            type="default"
                            icon={<EditOutlined />}
                            onClick={(e) => { e.stopPropagation(); navigate(`/areas/new?areaId=${record.id}`); }}
                        >
                            <span className="area-action-text">Edit</span>
                        </Button>
                        <Button
                            size="small"
                            type="default"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={hasChildren}
                            title={hasChildren ? 'Cannot delete area with children' : 'Delete area'}
                            onClick={(e) => { e.stopPropagation(); handleDelete(record.id, record.name); }}
                        >
                            <span className="area-action-text">Delete</span>
                        </Button>
                    </Space>
                );
            },
        },
    ], [navigate, handleDelete, handleArchive]);

    // ── Map centering ─────────────────────────────────────────────────────────

    useEffect(() => {
        const geom = getGpsGeometryForAreaMap(selectedArea, selectedArea, slamMap);
        if (!geom?.coordinates?.length) return;
        if (geom.type === 'Point') {
            const [lng, lat] = geom.coordinates as number[];
            setViewState({ longitude: lng, latitude: lat, zoom: 15, bearing: 0, pitch: 0 });
        } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
            const coords = geom.coordinates[0];
            if (Array.isArray(coords) && coords.length > 0) {
                const lon = coords.reduce((sum: number, c: any) => sum + c[0], 0) / coords.length;
                const lat = coords.reduce((sum: number, c: any) => sum + c[1], 0) / coords.length;
                setViewState({ longitude: lon, latitude: lat, zoom: 15, bearing: 0, pitch: 0 });
            }
        }
    }, [selectedArea, slamMap]);

    // ── Tree with add-buttons ─────────────────────────────────────────────────

    const areasWithAddButtons = useMemo(() => {
        const addButtonRow = (parentId: number, parentName: string): AddButtonRow => ({
            id: `add-${parentId}`, name: '', isAddButton: true, parentId, parentName,
        });
        const processArea = (area: TableRow, depth: number = 0): TableRow => {
            if ('isAddButton' in area && area.isAddButton) return area;
            const areaRecord = area as Area;
            const processed = { ...areaRecord, hierarchyDepth: depth };
            if (areaRecord.children && areaRecord.children.length > 0) {
                processed.children = areaRecord.children.map((child) => processArea(child, depth + 1));
            } else {
                processed.children = [];
            }
            if (selectedArea?.id === areaRecord.id && !areaRecord.archivedAt) {
                const buttonRow = addButtonRow(areaRecord.id, areaRecord.name);
                processed.children = [...processed.children, { ...buttonRow, hierarchyDepth: depth + 1 }];
            }
            return processed;
        };
        return areas.map((area) => processArea(area, 0));
    }, [areas, selectedArea]);

    // ── Areas to display in right panel ──────────────────────────────────────

    const areasToDisplay = useMemo(() => {
        if (!selectedArea) return [];
        const collectChildren = (area: Area, depth: number = 0): Array<Area & { hierarchyDepth: number }> => {
            const result = [{ ...area, hierarchyDepth: depth }];
            if (area.children && area.children.length > 0) {
                area.children.forEach(child => {
                    if ('isAddButton' in child && child.isAddButton) return;
                    result.push(...collectChildren(child as Area, depth + 1));
                });
            }
            return result;
        };
        return collectChildren(selectedArea);
    }, [selectedArea]);

    const gpsAreasToDisplay = useMemo(() => (
        areasToDisplay
            .map((area) => {
                const geom = getGpsGeometryForAreaMap(area, selectedArea, slamMap);
                return geom ? { ...area, geom } : null;
            })
            .filter((area): area is Area & { hierarchyDepth: number; geom: any } => !!area)
    ), [areasToDisplay, selectedArea, slamMap]);

    // ── Area type template images ─────────────────────────────────────────────

    useEffect(() => {
        if (!areasToDisplay.length) return;
        (async () => {
            const newTypeIds = new Set<number>();
            areasToDisplay.forEach(area => {
                if (area.areaType?.id && !checkedAreaTypes.has(area.areaType.id)) {
                    newTypeIds.add(area.areaType.id);
                }
            });
            if (newTypeIds.size === 0) return;
            const updatedTemplateMap = new Map(areaTypeTemplates);
            const updatedCheckedTypes = new Set(checkedAreaTypes);
            for (const typeId of newTypeIds) {
                updatedCheckedTypes.add(typeId);
                try {
                    const typeRes = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${typeId}`, { withCredentials: true });
                    const hasTemplate = typeRes.data?.data?.templateImage || typeRes.data?.templateImage;
                    if (hasTemplate) {
                        updatedTemplateMap.set(typeId, `${ENDPOINTS.AREA_TYPES_V2}/${typeId}/template-image?t=${Date.now()}`);
                    }
                } catch { /* ignore */ }
            }
            setCheckedAreaTypes(updatedCheckedTypes);
            setAreaTypeTemplates(updatedTemplateMap);
        })();
    }, [areasToDisplay, areaTypeTemplates, checkedAreaTypes]);

    // ─── Render ───────────────────────────────────────────────────────────────

    const isIndoorLike = selectedArea?.spaceType === 'indoor' || selectedArea?.spaceType === 'mixed';

    // Build SlamOverlayData + SlamPlacement from the fetched slamMap for the GPS map
    // Uses slamBlobUrl (fetched via backend proxy) instead of the raw GCS signed URL
    // to avoid CORS issues when Mapbox loads the image into WebGL.
    const slamOverlayData = useMemo(() => {
        if (!slamBlobUrl || !slamMap) return null;
        // Use fallback field names matching AreaCreate (widthPixels/heightPixels/resolution)
        const sm = slamMap as any;
        const imgW = slamMap.imageWidth || sm.widthPixels;
        const imgH = slamMap.imageHeight || sm.heightPixels;
        const mpp = slamMap.metersPerPixel || sm.resolution;
        if (!imgW || !imgH || !mpp) return null;
        return {
            imageUrl: slamBlobUrl,
            imageWidth: Number(imgW),
            imageHeight: Number(imgH),
            metersPerPixel: Number(mpp),
            geoAnchor: slamMap.geoAnchor || null,
        };
    }, [slamMap, slamBlobUrl]);

    const slamPlacementData = useMemo(() => {
        // If the SLAM map has a geo-anchor (mixed/anchored), SlamMapOverlay uses it directly
        if (slamOverlayData?.geoAnchor) return null;

        // Use saved placement from area configurations (set during area creation/editing)
        const saved = getSavedSlamPlacement(selectedArea);
        if (saved) return saved;

        // Fallback: GPS point geometry only. Indoor local metre points must not be
        // treated as longitude/latitude.
        if (selectedArea?.spaceType !== 'indoor' && selectedArea?.geom?.type === 'Point' && isGpsGeometry(selectedArea.geom)) {
            const [lng, lat] = selectedArea.geom.coordinates as number[];
            return { lat, lng, bearingDegrees: 0 };
        }
        return null;
    }, [slamOverlayData, selectedArea]);

    return (
        <div className="areas-page">
            <Row justify="start" align="middle" className="areas-header">
                <Col>
                    <Space direction="vertical" size={0}>
                        <Title level={2} style={{ margin: 0 }}>Areas</Title>
                        <Text type="secondary">
                            Manage GPS areas, indoor spaces, and mixed environments for your company.
                        </Text>
                    </Space>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                {/* ── Left: Area Tree ── */}
                <Col xs={24} xl={11}>
                    <Card>
                        <Checkbox checked={showArchived} onChange={e => setShowArchived(e.target.checked)} style={{ marginBottom: 12 }}>Show archived areas</Checkbox>
                        <Table
                            loading={loading}
                            dataSource={areasWithAddButtons}
                            columns={columns}
                            size="small"
                            className="areas-table"
                            rowKey={(record) => String(record.id)}
                            expandable={{
                                childrenColumnName: 'children',
                                expandedRowKeys,
                                showExpandColumn: false,
                                onExpand: (expanded, record: any) => {
                                    if (record.isAddButton) return;
                                    const recordKey = String(record.id);
                                    if (expanded) {
                                        setExpandedRowKeys([...expandedRowKeys, recordKey]);
                                    } else {
                                        setExpandedRowKeys(expandedRowKeys.filter(key => key !== recordKey));
                                    }
                                }
                            }}
                            pagination={false}
                            locale={{ emptyText: <Empty description="No areas found" /> }}
                            onRow={(record: TableRow) => ({
                                onClick: () => {
                                    if ('isAddButton' in record && record.isAddButton) return;
                                    const areaRecord = record as Area;
                                    fetchAreaDetail(areaRecord.id, areas.filter((a): a is Area => !('isAddButton' in a)));
                                    const path = findAreaPath(areaRecord.id, areas);
                                    const pathKeys = path.map((id) => String(id));
                                    const descendantKeys = collectExpandedKeys(areaRecord);
                                    setExpandedRowKeys(Array.from(new Set([...pathKeys, ...descendantKeys])));
                                },
                                style: ('isAddButton' in record && record.isAddButton)
                                    ? { background: '#fafafa', cursor: 'default' }
                                    : undefined
                            })}
                            rowClassName={(record: TableRow) => {
                                if ('isAddButton' in record && record.isAddButton) return 'area-add-button-row';
                                const areaRecord = record as Area;
                                const classes = [];
                                if (selectedArea?.id === areaRecord.id) classes.push('area-row-selected');
                                if ((areaRecord.hierarchyDepth || 0) > 0) classes.push('area-row-child');
                                return classes.join(' ');
                            }}
                        />
                        <div className="areas-new-parent-row list-create-row">
                            <Button
                                type="text"
                                icon={<PlusOutlined />}
                                className="areas-new-button areas-new-parent-button list-create-button"
                                onClick={() => navigate('/areas/new')}
                            >
                                Create New Parent Area
                            </Button>
                        </div>
                    </Card>
                </Col>

                {/* ── Right: Detail Panel ── */}
                <Col xs={24} xl={13}>
                    {/* GPS Map — always shown for outdoor/mixed; also shown for indoor as a reference pin */}
                    <Card title="Area map" className="areas-map-card">
                        <div className="areas-map-wrapper">
                            <AreaMap
                                mapKey={selectedArea?.id}
                                areaGeometry={getGpsGeometryForAreaMap(selectedArea, selectedArea, slamMap)}
                                areaName={selectedArea?.name}
                                initialViewState={viewState}
                                allAreas={gpsAreasToDisplay}
                                areaType={selectedArea?.areaType}
                                areaTypeTemplates={areaTypeTemplates}
                                slamOverlay={slamOverlayData}
                                slamPlacement={slamPlacementData}
                            />
                        </div>
                    </Card>

                    {/* SLAM Map panel — shown for indoor or mixed areas */}
                    {isIndoorLike && (
                        <Card
                            title={
                                <Space>
                                    {selectedArea?.spaceType === 'mixed'
                                        ? <PartitionOutlined style={{ color: '#722ed1' }} />
                                        : <HomeOutlined style={{ color: '#fa8c16' }} />}
                                    <span>SLAM Map</span>
                                    {selectedArea?.spaceType === 'mixed'
                                        ? <Tag color="purple">mixed (anchored)</Tag>
                                        : <Tag color="orange">indoor</Tag>}
                                </Space>
                            }
                            style={{ marginTop: 16 }}
                            className="areas-slam-map-card"
                            size="small"
                        >
                            {slamMapLoading ? (
                                <div className="areas-slam-map-loading">
                                    <Spin size="small" />
                                    <Text type="secondary" style={{ marginLeft: 8 }}>Loading SLAM map…</Text>
                                </div>
                            ) : slamMap ? (
                                <div className="areas-slam-map-content">
                                    <div className="areas-slam-map-meta">
                                        <Text strong>{slamMap.name}</Text>
                                        <Tag style={{ marginLeft: 8 }}>{slamMap.mapType}</Tag>
                                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                                            {new Date(slamMap.createdAt).toLocaleDateString()}
                                        </Text>
                                    </div>
                                    {slamMap.imageUrl ? (
                                        <div className="areas-slam-map-image-wrapper">
                                            <Image
                                                src={slamMap.imageUrl}
                                                alt={slamMap.name}
                                                style={{ maxWidth: '100%', borderRadius: 8 }}
                                                placeholder={<Spin />}
                                            />
                                        </div>
                                    ) : (
                                        <Alert
                                            type="info"
                                            showIcon
                                            message="SLAM map exists but no image has been uploaded yet."
                                            style={{ marginTop: 8 }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <Text type="secondary">
                                            No SLAM map linked yet. Assign one when editing this area.
                                        </Text>
                                    }
                                />
                            )}
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default Areas;
